import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { fmtTs } from '../lib/globalTz';

type CheckStatus = 'ok' | 'warning' | 'error' | 'unknown';

interface HealthCheck {
  id: string;
  label: string;
  status: CheckStatus;
  message: string;
  detail?: unknown;
}

interface OpsStatus {
  generatedAt: string;
  integrationHealth: HealthCheck[];
app: {
  name?: string;
  nodeEnv: string;
  deployEnvironment?: string;
  releaseChannel?: string;
  version: string;
  buildNumber?: string;
  buildTime: string;
  buildSource?: string;
  buildReference?: string;
  versionManifestPath?: string;
  sourceControlMode?: string;
  sourceControlLabel?: string;
  sourceControlMessage?: string;
  nodeVersion?: string;
  processStartedAt: string;
  uptimeSeconds: number;
};
}


interface BackupJob {
  id: string;
  job_type: string;
  status: 'queued' | 'running' | 'success' | 'failed' | string;
  requested_by?: string | null;
  requested_by_email?: string | null;
  stdout?: string | null;
  stderr?: string | null;
  error?: string | null;
  started_at: string;
  finished_at?: string | null;
}

interface BackupJobResponse {
  ok: boolean;
  job: BackupJob | null;
}

function isBackupRunning(job: BackupJob | null): boolean {
  return Boolean(job && ['queued', 'running'].includes(String(job.status).toLowerCase()));
}



function statusClass(status: CheckStatus): string {
  switch (status) {
    case 'ok':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900';
    case 'warning':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900';
    case 'error':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700';
  }
}

function pretty(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function HealthCard({ check }: { check: HealthCheck }) {
  return (
    <div className={`rounded-xl border p-4 ${statusClass(check.status)}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-bold text-sm">{check.label}</div>
          <div className="text-xs mt-1">{check.message}</div>
        </div>
        <span className="uppercase text-[10px] font-bold px-2 py-1 rounded-full bg-white/70 dark:bg-black/20">
          {check.status}
        </span>
      </div>

      {check.detail !== undefined && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-semibold">Details</summary>
          <pre className="mt-2 text-[11px] overflow-auto max-h-44 whitespace-pre-wrap">
            {pretty(check.detail)}
          </pre>
        </details>
      )}
    </div>
  );
}

export function MastersOpsPanel() {
  const [ops, setOps] = useState<OpsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [backupJob, setBackupJob] = useState<BackupJob | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadOps() {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<OpsStatus>('/masters/ops/status');
      setOps(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }


  async function loadLatestBackupJob() {
    try {
      const data = await apiFetch<BackupJobResponse>('/masters/ops/backup-jobs/latest');
      setBackupJob(data.job);
      setRunningBackup(isBackupRunning(data.job));
    } catch {
      // The operations status call still surfaces backup health. Avoid masking it with polling noise.
    }
  }

  async function loadBackupJob(jobId: string) {
    const data = await apiFetch<BackupJobResponse>(`/masters/ops/backup-jobs/${encodeURIComponent(jobId)}`);
    setBackupJob(data.job);
    setRunningBackup(isBackupRunning(data.job));
    return data.job;
  }



  async function runBackupNow() {
    const confirmed = window.confirm('Run KloudSecure backup now? This will run in the background and may take several minutes.');
    if (!confirmed) return;

    setRunningBackup(true);
    setMessage(null);
    setError(null);

    try {
      const result = await apiFetch<{ ok: boolean; jobId: string; status?: string; message?: string; alreadyRunning?: boolean }>('/masters/ops/run-backup', {
        method: 'POST',
      });

      setMessage(result.message || `Backup started. Job ID: ${result.jobId}`);
      const job = await loadBackupJob(result.jobId);
      setRunningBackup(isBackupRunning(job));
      await loadOps();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadOps();
      await loadLatestBackupJob();
      setRunningBackup(false);
    }
  }

  async function refreshCustomerCache() {
    setMessage(null);
    setError(null);

    try {
      const result = await apiFetch<{ ok: boolean; message?: string; supported?: boolean }>('/masters/ops/refresh-customer-cache', {
        method: 'POST',
      });

      setMessage(result.message || 'Customer cache refresh completed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function reloadMetadataCache() {
    setMessage(null);
    setError(null);

    try {
      const result = await apiFetch<{ ok: boolean; message?: string }>('/masters/ops/reload-metadata-cache', {
        method: 'POST',
      });

      setMessage(result.message || 'Metadata cache reload completed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void loadOps();
    void loadLatestBackupJob();
  }, []);

  useEffect(() => {
    if (!backupJob || !isBackupRunning(backupJob)) return;

    const timer = window.setInterval(() => {
      void loadBackupJob(backupJob.id).then((job) => {
        if (job && !isBackupRunning(job)) {
          void loadOps();
        }
      }).catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
      });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [backupJob?.id, backupJob?.status]);

  return (
    <div className="space-y-4">
      <section className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-foreground">Integration Health</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Validates platform integrations without exposing secret values.
            </p>
          </div>

          <button
            className="px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
            onClick={() => void loadOps()}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Health'}
          </button>
        </div>

        {ops && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {ops.integrationHealth.map((check) => (
              <HealthCard key={check.id} check={check} />
            ))}
          </div>
        )}

        {ops && (
          <div className="mt-4 rounded-xl border border-border p-4 text-xs text-muted-foreground">
            <div className="font-bold text-foreground mb-2">App Version / Runtime</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              <div>Application: {ops.app.name || 'KloudSecure AI SOC Dashboard'}</div>
              <div>Environment: {ops.app.deployEnvironment || ops.app.nodeEnv}</div>
              <div>Release channel: {ops.app.releaseChannel || 'alpha'}</div>
              <div>Version: {ops.app.version || '0.1.0-alpha.local'}</div>
              <div>Build number: {ops.app.buildNumber || 'not stamped'}</div>
              <div>Build time: {ops.app.buildTime || 'not stamped'}</div>
              <div>Build source: {ops.app.buildSource || 'Runtime defaults'}</div>
              <div>Build reference: {ops.app.buildReference || 'not stamped'}</div>
              <div>Source control: {ops.app.sourceControlLabel || 'Disabled on runtime'}</div>
              <div>Node.js: {ops.app.nodeVersion || 'unknown'}</div>
              <div>Process started: {fmtTs(ops.app.processStartedAt)}</div>
              <div>Uptime: {ops.app.uptimeSeconds}s</div>
            </div>
            {ops.app.sourceControlMessage && (
              <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300 p-2">
                {ops.app.sourceControlMessage}
              </div>
            )}
            {ops.app.versionManifestPath && ops.app.versionManifestPath !== 'not found' && (
              <div className="mt-2 text-[11px] opacity-80">Version manifest: {ops.app.versionManifestPath}</div>
            )}
            <div className="mt-2">Last health refresh: {fmtTs(ops.generatedAt)}</div>
          </div>
        )}
      </section>

      <section className="bg-white dark:bg-card rounded-xl shadow p-5">
        <h3 className="text-sm font-bold text-foreground">Maintenance</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Run operational maintenance actions. All write actions are audited.
        </p>

        {message && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300 p-3 text-sm">
            {message}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 text-red-700 dark:text-red-300 p-3 text-sm">
            {error}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={() => void runBackupNow()}
            disabled={runningBackup}
          >
            {runningBackup ? 'Running backup...' : 'Run Backup Now'}
          </button>

          <button
            className="px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
            onClick={() => void refreshCustomerCache()}
          >
            Refresh Customer Cache
          </button>

          <button
            className="px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
            onClick={() => void reloadMetadataCache()}
          >
            Rebuild / Reload Metadata Cache
          </button>
        </div>

        {backupJob && (
          <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-foreground">Backup job #{backupJob.id}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Started: {fmtTs(backupJob.started_at)}
                  {backupJob.finished_at ? ` • Finished: ${fmtTs(backupJob.finished_at)}` : ''}
                </div>
              </div>
              <span className={`uppercase text-[10px] font-bold px-2 py-1 rounded-full border ${statusClass(
                backupJob.status === 'success' ? 'ok' : backupJob.status === 'failed' ? 'error' : 'warning',
              )}`}>
                {backupJob.status}
              </span>
            </div>

            {backupJob.error && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-xs text-red-700 dark:text-red-300">
                {backupJob.error}
              </div>
            )}

            {(backupJob.stdout || backupJob.stderr) && (
              <details className="mt-3" open={isBackupRunning(backupJob)}>
                <summary className="cursor-pointer text-xs font-semibold text-foreground">Backup live log / tail</summary>
                {backupJob.stdout && (
                  <pre className="mt-2 text-[11px] overflow-auto max-h-64 whitespace-pre-wrap rounded-lg bg-black/80 text-slate-100 p-3">
                    {backupJob.stdout}
                  </pre>
                )}
                {backupJob.stderr && (
                  <pre className="mt-2 text-[11px] overflow-auto max-h-44 whitespace-pre-wrap rounded-lg bg-red-950/80 text-red-100 p-3">
                    {backupJob.stderr}
                  </pre>
                )}
              </details>
            )}
          </div>
        )}
      </section>    </div>
  );
}
